import React, { useEffect, useState } from "react";
import { Image, Space, message, Table as AntTable } from "antd";
import { useDispatch, useSelector } from "react-redux";
import DeleteMemberModal from "../../../../components/common/modalMember";
import Button from "../../../../components/common/button";
import View from "../../../../assets/icons/view.svg";
import { ApartmentOutlined, MenuOutlined } from "@ant-design/icons";
import SubCategoryChildModal from "../subservice/subCategoryChildModal";
import AddTypeCard from "../../business-types/manage-types/AddTypeCard";
import colors from "../../../../share/constants/constants";
import { fetchCategories } from "../../../../store/slices/categorySlice";
import { fetchSubCategories } from "../../../../store/slices/subCategorySlice";
import Trash from "../../../../assets/icons/trash.svg";
import { 
  updateServiceCategory, 
  updateSubserviceCategory,
  updateCategoriesOrder,
  updateSubcategoriesOrder
} from "../../../../api/service";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableCategoryRow = ({ children, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props['data-row-key'],
  });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <tr {...props} ref={setNodeRef} style={style}>
      {React.Children.map(children, (child, index) => {
        // Index 0 is the expand icon column - preserve it completely
        // Index 1 is our sort/drag handle column - modify this one
        if (index === 1 && child && React.isValidElement(child)) {
          return React.cloneElement(child, {
            ...child.props,
            children: (
              <div 
                {...attributes} 
                {...listeners} 
                style={{ cursor: 'grab', display: 'inline-block', touchAction: 'none' }}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              >
                <MenuOutlined style={{ color: colors.primaryPurple }} />
              </div>
            ),
          });
        }
        // For all other columns (including index 0 - expand icon), return as-is
        return child;
      })}
    </tr>
  );
};

const SortableSubCategoryRow = ({ children, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props['data-row-key'],
  });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <tr {...props} ref={setNodeRef} style={style}>
      {React.Children.map(children, (child, index) => {
        if (index === 0 && child) {
          return React.cloneElement(child, {
            ...child.props,
            children: (
              <div {...attributes} {...listeners} style={{ cursor: 'grab', display: 'inline-block' }}>
                <MenuOutlined style={{ color: colors.primaryPurple }} />
              </div>
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

const Categories = ({ newChange }) => {
  const [result, setResult] = useState([]);
  const [editValues, setEditValues] = useState();
  const [deleteId, setDeleteId] = useState();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddTypeModalOpen, setIsAddTypeModalOpen] = useState(false);
  const [childModal, setChildModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const dispatch = useDispatch();

  const {
    isLoading: loading,
    res: categories,
  } = useSelector((state) => state?.category);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Only activate after 8px of movement
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    dispatch(fetchCategories());
  }, [isModalOpen, dispatch]);

  useEffect(() => {
    if (categories) {
      // Sort by order if available, otherwise maintain current order
      const sorted = [...categories].sort((a, b) => {
        const orderA = a.order !== undefined ? a.order : Infinity;
        const orderB = b.order !== undefined ? b.order : Infinity;
        return orderA - orderB;
      });
      setResult(sorted);
    }
  }, [categories]);

  useEffect(() => {
    dispatch(fetchCategories());
  }, [isAddTypeModalOpen, dispatch]);

  useEffect(() => {
    dispatch(fetchSubCategories());
  }, [dispatch]);

  const YesClickHandler = async () => {
    if (editValues?.isSubCategory) {
      const deleteCategory = await updateSubserviceCategory(deleteId, {
        isDelete: true,
      });
      if (deleteCategory) {
        message.success("Sub Category Deleted Successfully");
        setDeleteId(null);
      }
    } else {
      const deleteCategory = await updateServiceCategory(deleteId, {
        display: false,
      });
      if (deleteCategory) {
        message.success("Category Deleted Successfully");
        setDeleteId(null);
      }
    }
    setIsModalOpen(false);
  };

  const hideModal = () => {
    setIsModalOpen(false);
  };

  const handleAddBusiness = () => {
    setEditValues(null);
    setIsAddTypeModalOpen(true);
  };

  const handleEditBusiness = (values) => {
    setEditValues(values);
    setIsAddTypeModalOpen(true);
  };

  const handleDeleteCategory = (values) => {
    setIsModalOpen(true);
    setDeleteId(values?._id);
  };

  const handleCategoryDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id && over) {
      const oldIndex = result.findIndex((item) => item._id === active.id);
      const newIndex = result.findIndex((item) => item._id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newResult = arrayMove(result, oldIndex, newIndex);
        setResult(newResult);

        // Update order in backend
        const orderUpdates = newResult.map((item, index) => ({
          id: item._id,
          order: index + 1,
        }));

        try {
          // Backend expects an object with 'items' field
          await updateCategoriesOrder({ items: orderUpdates });
          message.success("Category order updated successfully");
          dispatch(fetchCategories());
        } catch (error) {
          message.error("Failed to update category order");
          // Revert on error
          setResult(result);
        }
      }
    }
  };

  const handleSubCategoryDragEnd = async (event, parentCategoryId) => {
    const { active, over } = event;

    if (active.id !== over?.id && over) {
      const parentCategory = result.find((cat) => cat._id === parentCategoryId);
      if (!parentCategory?.subServiceCategories) return;

      const subCategories = [...parentCategory.subServiceCategories];
      const oldIndex = subCategories.findIndex((item) => item._id === active.id);
      const newIndex = subCategories.findIndex((item) => item._id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newSubCategories = arrayMove(subCategories, oldIndex, newIndex);
        
        // Update local state
        const updatedResult = result.map((cat) =>
          cat._id === parentCategoryId
            ? { ...cat, subServiceCategories: newSubCategories }
            : cat
        );
        setResult(updatedResult);

        // Update order in backend
        const orderUpdates = newSubCategories.map((item, index) => ({
          id: item._id,
          order: index + 1,
        }));

        try {
          // Backend expects an object with 'items' field
          await updateSubcategoriesOrder({ subcategories: orderUpdates });
          message.success("Subcategory order updated successfully");
          dispatch(fetchCategories());
        } catch (error) {
          message.error("Failed to update subcategory order");
          // Revert on error
          setResult(result);
        }
      }
    }
  };

  const columns = [
    {
      title: "",
      key: "sort",
      width: 50,
      render: () => <MenuOutlined style={{ color: colors.primaryPurple }} />,
    },
    {
      title: "Category Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Category Image",
      dataIndex: "image",
      key: "image",
      render: (text, record) => (
        <>
          {record?.image ? (
            <Space>
              <Image
                src={record?.image}
                width={80}
                height={50}
                preview={true}
                alt="attachment"
                className="rounded object-cover"
              />
            </Space>
          ) : (
            <>N/A</>
          )}
        </>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 150,
      render: (ele) => (
        <div className="flex space-x-2">
          <div
            className="w-4 m-auto grid justify-center h-4 pt-1 rounded-full bg-lightLavender cursor-pointer"
            onClick={() => handleEditBusiness(ele)}
          >
            <img src={View} alt="View" />
          </div>
          <div
            className="flex items-center justify-center w-4 h-4  rounded-full bg-lightLavender cursor-pointer"
            onClick={() => handleDeleteCategory(ele)}
          >
            <img src={Trash} alt="View" />
          </div>
        </div>
      ),
    },
  ];

  const handleEditSubCategory = (values) => {
    setEditValues({ ...values, isSubCategory: true });
    setIsAddTypeModalOpen(true);
  };

  const handleDeleteSubCategory = (values) => {
    setEditValues({ ...values, isSubCategory: true });
    setIsModalOpen(true);
    setDeleteId(values?._id);
  };

  const handleSubCategoryChild = (el) => {
    setSelectedId(el);
    setChildModalOpen(true);
  };

  const subCategoriesColumns = [
    {
      title: "",
      key: "sort",
      width: 50,
      render: () => <MenuOutlined style={{ color: colors.primaryPurple }} />,
    },
    {
      title: "Sub-Category Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Sub-Category Image",
      dataIndex: "image",
      key: "image",
      render: (text, record) => (
        <>
          {record?.image ? (
            <Space>
              <Image
                src={record?.image}
                width={60}
                height={40}
                preview={true}
                alt="attachment"
                className="rounded object-cover"
              />
            </Space>
          ) : (
            <>N/A</>
          )}
        </>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 150,
      render: (ele) => (
        <Space size="middle">
          <div
            className="w-4 m-auto grid items-center justify-center h-4 rounded-full bg-lightLavender cursor-pointer"
            onClick={() => handleEditSubCategory(ele)}
          >
            <img src={View} alt="View" />
          </div>
          <div
            className="w-4 m-auto grid items-center justify-center h-4 rounded-full bg-lightLavender cursor-pointer"
            onClick={() => handleSubCategoryChild(ele)}
          >
            <ApartmentOutlined />
          </div>
          <div
            className="flex items-center justify-center w-4 h-4 rounded-full bg-lightLavender cursor-pointer"
            onClick={() => handleDeleteSubCategory(ele)}
          >
            <img src={Trash} alt="View" />
          </div>
        </Space>
      ),
    },
  ];

  const customHeaderStyle = {
    backgroundColor: colors.lightLavender,
    color: colors.primaryPurple,
  };

  const modifiedColumns = columns.map((col) => ({
    ...col,
    onHeaderCell: (column) => ({
      style: customHeaderStyle,
    }),
  }));

  const modifiedSubCategoriesColumns = subCategoriesColumns.map((col) => ({
    ...col,
    onHeaderCell: (column) => ({
      style: customHeaderStyle,
    }),
  }));

  return (
    <div className="grid gap-2 w-full">
      <div className="flex justify-end">
        <div className="mr-2">
          <Button
            title="Add New Category"
            className="w-15.8 h-3.8"
            color={colors.primaryPurple}
            borderRadius={5}
            onClick={handleAddBusiness}
          />
        </div>
      </div>
      <div className="container p-2">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleCategoryDragEnd}
        >
          <SortableContext
            items={result.map((item) => item._id)}
            strategy={verticalListSortingStrategy}
          >
            <AntTable
              dataSource={result}
              columns={modifiedColumns}
              rowKey="_id"
              pagination={false}
              loading={loading}
              components={{
                body: {
                  row: SortableCategoryRow,
                },
              }}
              expandable={{
                expandedRowRender: (record) => {
                  const sortedSubCategories = record.subServiceCategories
                    ? [...record.subServiceCategories].sort((a, b) => {
                        const orderA = a.order !== undefined ? a.order : Infinity;
                        const orderB = b.order !== undefined ? b.order : Infinity;
                        return orderA - orderB;
                      })
                    : [];

                  return (
                    <div className="pl-12 pr-4 py-4">
                      {sortedSubCategories.length > 0 ? (
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={(event) => handleSubCategoryDragEnd(event, record._id)}
                        >
                          <SortableContext
                            items={sortedSubCategories.map((item) => item._id)}
                            strategy={verticalListSortingStrategy}
                          >
                            <AntTable
                              dataSource={sortedSubCategories}
                              columns={modifiedSubCategoriesColumns}
                              pagination={false}
                              rowKey="_id"
                              className="nested-table"
                              expandable={{ expandIcon: () => null }}
                              components={{
                                body: {
                                  row: SortableSubCategoryRow,
                                },
                              }}
                            />
                          </SortableContext>
                        </DndContext>
                      ) : (
                        <p className="text-gray-500 italic">
                          No sub-categories available
                        </p>
                      )}
                    </div>
                  );
                },
                rowExpandable: (record) =>
                  record.subServiceCategories &&
                  record.subServiceCategories.length > 0,
              }}
              className="shadow-md rounded-md"
              rowClassName="custom-row"
            />
          </SortableContext>
        </DndContext>
      </div>

      {isModalOpen && (
        <DeleteMemberModal
          title={editValues?.isSubCategory ? "Delete Sub Category" : "Delete Category"}
          text={editValues?.isSubCategory 
            ? "Are you sure you want to delete this sub category?"
            : "Are you sure you want to delete this category?"}
          onCancel={hideModal}
          onOk={YesClickHandler}
        />
      )}

      <AddTypeCard
        visible={isAddTypeModalOpen}
        onClose={() => {
          setIsAddTypeModalOpen(false);
          dispatch(fetchCategories());
        }}
        editValues={editValues}
        category={!editValues?.isSubCategory}
        subservice={editValues?.isSubCategory}
      />

      <SubCategoryChildModal
        visible={childModal}
        data={selectedId}
        onCancel={() => {
          setChildModalOpen(false);
          dispatch(fetchSubCategories());
        }}
        category={false}
      />
    </div>
  );
};

export default Categories;
